import React, { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Settings,
  Download,
  FileText,
  Plus,
  Edit2,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
} from 'lucide-react';

// Job Categories Mapping
const JOB_CATEGORIES = {
  'Board of Directors': { color: '#6b21a8' },
  'Clinical Leadership': { color: '#2E8B98' },
  'Physicians': { color: '#256F7A' },
  'Nursing': { color: '#62BB46' },
  'Allied Health': { color: '#4FA038' },
  'Research': { color: '#F26522' },
  'Administration': { color: '#DC3545' },
  'IT & Engineering': { color: '#6C757D' },
  'Billing & Finance': { color: '#256F7A' },
  'Front Office': { color: '#2E8B98' },
};

// Helper function to map titles to categories
const getCategoryForTitle = (title) => {
  if (!title) return JOB_CATEGORIES['Administration'];

  const titleLower = title.toLowerCase();

  if (titleLower.includes('board') || titleLower.includes('board chair') || titleLower.includes('board member') || titleLower.includes('non-executive')) {
    return JOB_CATEGORIES['Board of Directors'];
  }
  if (titleLower.includes('director') || titleLower.includes('chief') || titleLower.includes('vp')) {
    return JOB_CATEGORIES['Clinical Leadership'];
  }
  if (titleLower.includes('physician') || titleLower.includes('doctor') || titleLower.includes('md')) {
    return JOB_CATEGORIES['Physicians'];
  }
  if (titleLower.includes('nurse') || titleLower.includes('rn') || titleLower.includes('lpn')) {
    return JOB_CATEGORIES['Nursing'];
  }
  if (titleLower.includes('therapist') || titleLower.includes('technician') || titleLower.includes('specialist')) {
    return JOB_CATEGORIES['Allied Health'];
  }
  if (titleLower.includes('researcher') || titleLower.includes('scientist')) {
    return JOB_CATEGORIES['Research'];
  }
  if (titleLower.includes('administrator') || titleLower.includes('manager') || titleLower.includes('coordinator')) {
    return JOB_CATEGORIES['Administration'];
  }
  if (titleLower.includes('developer') || titleLower.includes('engineer') || titleLower.includes('it ')) {
    return JOB_CATEGORIES['IT & Engineering'];
  }
  if (titleLower.includes('accountant') || titleLower.includes('billing') || titleLower.includes('finance')) {
    return JOB_CATEGORIES['Billing & Finance'];
  }
  if (titleLower.includes('receptionist') || titleLower.includes('assistant') || titleLower.includes('clerk')) {
    return JOB_CATEGORIES['Front Office'];
  }

  return JOB_CATEGORIES['Administration'];
};

// Status Badge Component
function StatusBadge({ status }) {
  const statusStyles = {
    active: { bg: '#E8F5E9', text: '#62BB46', label: 'Active' },
    inactive: { bg: '#F5F5F5', text: '#6C757D', label: 'Inactive' },
    on_leave: { bg: '#FFF3E0', text: '#F26522', label: 'On Leave' },
    terminated: { bg: '#FFEBEE', text: '#DC3545', label: 'Terminated' },
  };

  const style = statusStyles[status] || statusStyles.inactive;

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: style.bg,
        color: style.text,
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'capitalize',
      }}
    >
      {style.label}
    </span>
  );
}

// Neumorphic Button Component
function Button({
  onClick,
  children,
  variant = 'default',
  size = 'md',
  disabled = false,
  title,
}) {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '600',
    fontSize: '14px',
    opacity: disabled ? 0.5 : 1,
  };

  const sizes = {
    sm: { padding: '6px 12px', fontSize: '12px' },
    md: { padding: '10px 16px', fontSize: '14px' },
    lg: { padding: '12px 24px', fontSize: '16px' },
  };

  const variants = {
    default: {
      background: '#F8F9FA',
      color: '#2E8B98',
      boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
      '&:active': {
        boxShadow: 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)',
      },
    },
    primary: {
      background: '#2E8B98',
      color: '#FFFFFF',
      boxShadow: '5px 5px 10px rgba(46,139,152,0.3), -5px -5px 10px rgba(255,255,255,0.5)',
      '&:active': {
        boxShadow: 'inset 3px 3px 6px rgba(0,100,150,0.3)',
      },
    },
    danger: {
      background: '#DC3545',
      color: '#FFFFFF',
      boxShadow: '5px 5px 10px rgba(220,53,69,0.3), -5px -5px 10px rgba(255,255,255,0.5)',
      '&:active': {
        boxShadow: 'inset 3px 3px 6px rgba(180,0,20,0.3)',
      },
    },
  };

  const style = {
    ...baseStyle,
    ...sizes[size],
    ...variants[variant],
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={style}
      onMouseDown={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow =
            variants[variant]['&:active'].boxShadow;
        }
      }}
      onMouseUp={(e) => {
        if (!disabled) {
          e.currentTarget.style.boxShadow = variants[variant].boxShadow;
        }
      }}
    >
      {children}
    </button>
  );
}

// Input Component
function Input({ value, onChange, placeholder, style }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        padding: '10px 14px',
        borderRadius: '8px',
        border: 'none',
        background: '#F8F9FA',
        boxShadow: 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)',
        fontSize: '14px',
        color: '#333',
        outline: 'none',
        transition: 'all 0.2s ease',
        ...style,
      }}
      onFocus={(e) => {
        e.target.style.boxShadow =
          'inset 2px 2px 4px rgba(186,199,216,0.3), inset -2px -2px 4px rgba(255,255,255,0.6)';
      }}
      onBlur={(e) => {
        e.target.style.boxShadow =
          'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)';
      }}
    />
  );
}

// Select Component
function Select({ value, onChange, options, placeholder, style }) {
  return (
    <select
      value={value}
      onChange={onChange}
      style={{
        padding: '10px 14px',
        borderRadius: '8px',
        border: 'none',
        background: '#F8F9FA',
        boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
        fontSize: '14px',
        color: '#333',
        outline: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ...style,
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// Main DirectoryTab Component
export default function DirectoryTab({
  personnel = [],
  onEdit = () => {},
  onDeactivate = () => {},
  onViewProfile = () => {},
  onAdd = () => {},
  user = {},
}) {
  // State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'first_name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState({
    avatar: true,
    employeeId: true,
    jobTitle: true,
    department: true,
    supervisor: true,
    email: true,
    phone: true,
    status: true,
    hireDate: true,
  });
  const [showColumnMenu, setShowColumnMenu] = useState(false);

  const itemsPerPage = 15;

  // Extract unique departments
  const departments = useMemo(() => {
    return Array.from(new Set(personnel.map((p) => p.department).filter(Boolean))).sort();
  }, [personnel]);

  // Filter and search logic
  const filteredData = useMemo(() => {
    return personnel.filter((person) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        `${person.first_name} ${person.last_name}`.toLowerCase().includes(searchLower) ||
        (person.job_title && person.job_title.toLowerCase().includes(searchLower)) ||
        (person.department && person.department.toLowerCase().includes(searchLower)) ||
        (person.email && person.email.toLowerCase().includes(searchLower)) ||
        (person.employee_id && person.employee_id.toLowerCase().includes(searchLower)) ||
        (person.phone && person.phone.includes(searchTerm));

      const matchesDepartment = !departmentFilter || person.department === departmentFilter;

      const matchesStatus =
        !statusFilter ||
        (person.status || 'active').toLowerCase() === statusFilter.toLowerCase();

      const matchesCategory =
        categoryFilter.length === 0 ||
        categoryFilter.some(
          (cat) => getCategoryForTitle(person.job_title)?.color === JOB_CATEGORIES[cat]?.color
        );

      return matchesSearch && matchesDepartment && matchesStatus && matchesCategory;
    });
  }, [personnel, searchTerm, departmentFilter, statusFilter, categoryFilter]);

  // Sorting logic
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'name') {
        aVal = `${a.first_name} ${a.last_name}`;
        bVal = `${b.first_name} ${b.last_name}`;
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIdx, startIdx + itemsPerPage);

  // Handlers
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleToggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === sortedData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedData.map((p) => p.id)));
    }
  };

  const handleSelectAllPage = () => {
    if (paginatedData.every((p) => selectedIds.has(p.id))) {
      paginatedData.forEach((p) => {
        selectedIds.delete(p.id);
      });
      setSelectedIds(new Set(selectedIds));
    } else {
      paginatedData.forEach((p) => {
        selectedIds.add(p.id);
      });
      setSelectedIds(new Set(selectedIds));
    }
  };

  const handleExportCSV = () => {
    const headers = [
      'Name',
      'Employee ID',
      'Job Title',
      'Department',
      'Supervisor',
      'Email',
      'Phone',
      'Status',
      'Hire Date',
    ];

    const rows = sortedData.map((person) => [
      `${person.first_name} ${person.last_name}`,
      person.employee_id || '',
      person.job_title || '',
      person.department || '',
      person.supervisor || '',
      person.email || '',
      person.phone || '',
      person.status || 'active',
      person.hire_date || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `directory-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    alert('PDF export is available through the Report Builder. Please use the Reports section.');
  };

  const handleToggleCategoryFilter = (category) => {
    setCategoryFilter((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  // Render Column Header with Sort
  const renderColumnHeader = (key, label) => {
    const isActive = sortConfig.key === key;
    return (
      <th
        onClick={() => handleSort(key)}
        style={{
          padding: '12px',
          textAlign: 'left',
          fontSize: '13px',
          fontWeight: '700',
          color: '#333',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: '1px solid #E0E0E0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {label}
          {isActive &&
            (sortConfig.direction === 'asc' ? (
              <ChevronUp size={14} />
            ) : (
              <ChevronDown size={14} />
            ))}
        </div>
      </th>
    );
  };

  return (
    <div style={{ padding: '24px', background: '#F8F9FA', minHeight: '100vh' }}>
      {/* Toolbar */}
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Top Row */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <Input
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search name, title, department, email..."
              style={{ width: '100%' }}
            />
          </div>

          <Select
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={departments.map((dept) => ({ value: dept, label: dept }))}
            placeholder="Department"
          />

          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'on_leave', label: 'On Leave' },
              { value: 'terminated', label: 'Terminated' },
            ]}
            placeholder="Status"
          />

          <div style={{ position: 'relative' }}>
            <Button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              size="md"
              variant="default"
              title="Column visibility"
            >
              <Settings size={16} />
            </Button>
            {showColumnMenu && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '8px',
                  background: '#FFF',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '180px',
                  padding: '8px',
                }}
              >
                {[
                  { key: 'avatar', label: 'Avatar' },
                  { key: 'employeeId', label: 'Employee ID' },
                  { key: 'jobTitle', label: 'Job Title' },
                  { key: 'department', label: 'Department' },
                  { key: 'supervisor', label: 'Supervisor' },
                  { key: 'email', label: 'Email' },
                  { key: 'phone', label: 'Phone' },
                  { key: 'status', label: 'Status' },
                  { key: 'hireDate', label: 'Hire Date' },
                ].map((col) => (
                  <label
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: '13px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key]}
                      onChange={() => toggleColumnVisibility(col.key)}
                      style={{ cursor: 'pointer' }}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <Button onClick={handleExportCSV} size="md" variant="default" title="Export as CSV">
            <Download size={16} />
            CSV
          </Button>

          <Button onClick={handleExportPDF} size="md" variant="default" title="Export as PDF">
            <FileText size={16} />
            PDF
          </Button>

          <Button
            onClick={() => setBulkSelectMode(!bulkSelectMode)}
            size="md"
            variant={bulkSelectMode ? 'primary' : 'default'}
            title="Toggle bulk select mode"
          >
            <Check size={16} />
          </Button>

          <Button onClick={onAdd} size="md" variant="primary" title="Add new employee">
            <Plus size={16} />
            Add Employee
          </Button>
        </div>

        {/* Category Filter Chips */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#666', fontWeight: '600' }}>Categories:</span>
          {Object.keys(JOB_CATEGORIES).map((category) => (
            <button
              key={category}
              onClick={() => handleToggleCategoryFilter(category)}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                border: categoryFilter.includes(category) ? '2px solid' : '1px solid #DDD',
                borderColor: categoryFilter.includes(category)
                  ? JOB_CATEGORIES[category].color
                  : '#DDD',
                background: categoryFilter.includes(category)
                  ? JOB_CATEGORIES[category].color + '20'
                  : '#FFF',
                color: categoryFilter.includes(category)
                  ? JOB_CATEGORIES[category].color
                  : '#666',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {bulkSelectMode && selectedIds.size > 0 && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            background: '#E3F2FD',
            borderRadius: '8px',
            borderLeft: '4px solid #2E8B98',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#2E8B98' }}>
            {selectedIds.size} selected
          </span>
          <Button size="md" variant="default">
            Edit Status
          </Button>
          <Button size="md" variant="default">
            Export Selected
          </Button>
          <Button
            size="md"
            variant="danger"
            onClick={() => {
              selectedIds.forEach((id) => {
                const person = personnel.find((p) => p.id === id);
                if (person) onDeactivate(person);
              });
              setSelectedIds(new Set());
            }}
          >
            Deactivate Selected
          </Button>
          <div style={{ marginLeft: 'auto' }}>
            <Button
              onClick={handleSelectAll}
              size="sm"
              variant="default"
              title={selectedIds.size === sortedData.length ? 'Deselect all' : 'Select all'}
            >
              {selectedIds.size === sortedData.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>
        </div>
      )}

      {/* Data Table */}
      {sortedData.length > 0 ? (
        <div
          style={{
            background: '#FFF',
            borderRadius: '8px',
            overflow: 'hidden',
            boxShadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead
                style={{
                  background: '#F5F5F5',
                }}
              >
                <tr>
                  {bulkSelectMode && (
                    <th style={{ padding: '12px', textAlign: 'center', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={
                          paginatedData.length > 0 &&
                          paginatedData.every((p) => selectedIds.has(p.id))
                        }
                        onChange={handleSelectAllPage}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                  )}
                  {renderColumnHeader('name', 'Name')}
                  {visibleColumns.employeeId && renderColumnHeader('employee_id', 'Employee ID')}
                  {visibleColumns.jobTitle && renderColumnHeader('job_title', 'Job Title')}
                  {visibleColumns.department && renderColumnHeader('department', 'Department')}
                  {visibleColumns.supervisor && renderColumnHeader('supervisor', 'Supervisor')}
                  {visibleColumns.email && renderColumnHeader('email', 'Email')}
                  {visibleColumns.phone && renderColumnHeader('phone', 'Phone')}
                  {visibleColumns.status && renderColumnHeader('status', 'Status')}
                  {visibleColumns.hireDate && renderColumnHeader('hire_date', 'Hire Date')}
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #E0E0E0', width: '80px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((person) => (
                  <tr
                    key={person.id}
                    onClick={() => onViewProfile(person)}
                    style={{
                      cursor: 'pointer',
                      transition: 'background 0.2s ease',
                      borderBottom: '1px solid #E0E0E0',
                      '&:hover': { background: '#F9F9F9' },
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F9F9F9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {bulkSelectMode && (
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(person.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(person.id);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                    )}
                    <td
                      style={{
                        padding: '12px',
                        fontSize: '14px',
                        color: '#333',
                        fontWeight: '600',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {visibleColumns.avatar && (
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: JOB_CATEGORIES[Object.keys(JOB_CATEGORIES)[0]].color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#FFF',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              flexShrink: 0,
                            }}
                          >
                            {`${person.first_name?.[0] || ''}${person.last_name?.[0] || ''}`}
                          </div>
                        )}
                        <div>
                          <div>{`${person.first_name} ${person.last_name}`}</div>
                        </div>
                      </div>
                    </td>
                    {visibleColumns.employeeId && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                        {person.employee_id}
                      </td>
                    )}
                    {visibleColumns.jobTitle && (
                      <td
                        style={{
                          padding: '12px',
                          fontSize: '13px',
                          color: '#666',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: getCategoryForTitle(person.job_title).color,
                              flexShrink: 0,
                            }}
                          />
                          {person.job_title}
                        </div>
                      </td>
                    )}
                    {visibleColumns.department && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                        {person.department}
                      </td>
                    )}
                    {visibleColumns.supervisor && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                        {person.supervisor}
                      </td>
                    )}
                    {visibleColumns.email && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#2E8B98' }}>
                        {person.email}
                      </td>
                    )}
                    {visibleColumns.phone && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                        {person.phone}
                      </td>
                    )}
                    {visibleColumns.status && (
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <StatusBadge status={person.status || 'active'} />
                      </td>
                    )}
                    {visibleColumns.hireDate && (
                      <td style={{ padding: '12px', fontSize: '13px', color: '#666' }}>
                        {person.hire_date ? new Date(person.hire_date).toLocaleDateString() : '-'}
                      </td>
                    )}
                    <td
                      style={{
                        padding: '12px',
                        textAlign: 'center',
                        fontSize: '13px',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => onEdit(person)}
                          style={{
                            padding: '6px 8px',
                            background: '#F8F9FA',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#2E8B98',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow:
                              '3px 3px 6px rgba(186,199,216,0.3), -3px -3px 6px rgba(255,255,255,0.6)',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.boxShadow =
                              'inset 2px 2px 4px rgba(186,199,216,0.3)';
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.boxShadow =
                              '3px 3px 6px rgba(186,199,216,0.3), -3px -3px 6px rgba(255,255,255,0.6)';
                          }}
                          title="Edit employee"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => onDeactivate(person)}
                          style={{
                            padding: '6px 8px',
                            background: '#FFEBEE',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#DC3545',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow:
                              '3px 3px 6px rgba(220,53,69,0.2), -3px -3px 6px rgba(255,255,255,0.6)',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => {
                            e.currentTarget.style.boxShadow =
                              'inset 2px 2px 4px rgba(220,53,69,0.2)';
                          }}
                          onMouseUp={(e) => {
                            e.currentTarget.style.boxShadow =
                              '3px 3px 6px rgba(220,53,69,0.2), -3px -3px 6px rgba(255,255,255,0.6)';
                          }}
                          title="Deactivate employee"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            style={{
              padding: '16px',
              background: '#F5F5F5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
              Page {currentPage} of {totalPages} ({sortedData.length} total)
            </span>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                size="sm"
                variant="default"
              >
                <ChevronLeft size={14} />
              </Button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  variant={currentPage === page ? 'primary' : 'default'}
                  size="sm"
                >
                  {page}
                </Button>
              ))}

              <Button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                size="sm"
                variant="default"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            background: '#FFF',
            borderRadius: '8px',
            padding: '60px 20px',
            textAlign: 'center',
            boxShadow:
              '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
          }}
        >
          <Search size={48} style={{ color: '#CCC', marginBottom: '16px', opacity: 0.5 }} />
          <h3 style={{ color: '#999', fontSize: '18px', marginBottom: '8px' }}>
            No employees found
          </h3>
          <p style={{ color: '#CCC', fontSize: '14px' }}>
            Try adjusting your filters or search criteria.
          </p>
        </div>
      )}
    </div>
  );
}
